import type { ReactNode } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type DetailPageSkeletonProps = {
  /**
   * One entry per section; the number is how many rows that section shows.
   * Detail rows are a label + value pair; form rows are a label + input.
   */
  sections?: number[];
  /** 'detail' = label/value pairs (read view); 'form' = label/input stacks (edit view). */
  variant?: 'detail' | 'form';
  /** Reserve a top-right action button (e.g. Edit/Save). */
  headerAction?: boolean;
  /** Optional trailing block — e.g. a JSON/code preview placeholder. */
  footer?: ReactNode;
  className?: string;
};

function DetailRowSkeleton() {
  return (
    <div className="flex items-center gap-4">
      <Skeleton className="h-4 w-32 shrink-0" />
      <Skeleton className="h-4 w-48 max-w-full" />
    </div>
  );
}

function FormRowSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

/**
 * Loading placeholder for detail and edit pages that render inside
 * TablePageLayout. Mirrors the page header plus a stack of titled sections so
 * the page settles into content without a centered spinner.
 */
export function DetailPageSkeleton({
  sections = [3, 2, 2, 1],
  variant = 'detail',
  headerAction = false,
  footer,
  className,
}: DetailPageSkeletonProps) {
  const RowSkeleton = variant === 'form' ? FormRowSkeleton : DetailRowSkeleton;

  return (
    <div className={cn('mr-auto w-full', className)} aria-hidden="true">
      {/* Page header (mirrors EditablePageHeader) */}
      <div className="flex flex-col gap-2 sm:gap-1">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>

      <div className="mt-6 flex flex-col gap-8">
        {headerAction && (
          <div className="flex justify-end">
            <Skeleton className="h-9 w-20" />
          </div>
        )}

        {sections.map((rows, sectionIdx) => (
          <div key={sectionIdx} className="flex flex-col gap-3">
            <Skeleton className="h-5 w-32" />
            <div className="flex flex-col gap-2.5">
              {Array.from({ length: rows }).map((_, rowIdx) => (
                <RowSkeleton key={rowIdx} />
              ))}
            </div>
          </div>
        ))}

        {footer}
      </div>
    </div>
  );
}
